// Helper push notifications — abstrage flow-ul complet de subscribe/unsubscribe.
// Folosit din componenta de prompt și din profil (toggle ON/OFF).

import api from '../services/api';

const urlBase64ToUint8Array = (base64String) => {
  // Web Push API cere applicationServerKey ca Uint8Array; cheia VAPID e base64-url-safe.
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

const isSupported = () => {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

const getCurrentPermission = () => {
  if (!isSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
};

// Verifică dacă există subscription activă pe ACEST browser/dispozitiv.
async function hasActiveSubscription() {
  if (!isSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch (_) {
    return false;
  }
}

// Cere permisiune utilizatorului + se abonează + trimite la backend.
// Întoarce { ok: true } sau { ok: false, error: 'reason' }.
async function subscribe() {
  if (!isSupported()) return { ok: false, error: 'unsupported' };

  // 1. Cere permisiune (apare prompt-ul nativ al browserului)
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, error: 'permission_denied' };

  try {
    // 2. Ia VAPID public key de la backend
    const { data } = await api.get('/push/vapid-public-key');
    if (!data?.publicKey) return { ok: false, error: 'no_vapid_key' };

    // 3. Service worker trebuie să fie activ (înregistrat în index.js doar în production)
    const reg = await navigator.serviceWorker.ready;

    // 4. Abonament — dacă există deja, browser-ul îl întoarce; altfel îl creează
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }

    // 5. Trimite la backend (formatul JSON include endpoint și keys)
    const subJson = sub.toJSON();
    await api.post('/push/subscribe', {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
    });

    return { ok: true };
  } catch (err) {
    console.error('[push] subscribe failed:', err);
    return { ok: false, error: err.message || 'unknown' };
  }
}

// Dezabonare locală + ștergere din backend.
async function unsubscribe() {
  if (!isSupported()) return { ok: false, error: 'unsupported' };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };

    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    await api.post('/push/unsubscribe', { endpoint }).catch(() => {});
    return { ok: true };
  } catch (err) {
    console.error('[push] unsubscribe failed:', err);
    return { ok: false, error: err.message || 'unknown' };
  }
}

const pushNotifications = {
  isSupported,
  getCurrentPermission,
  hasActiveSubscription,
  subscribe,
  unsubscribe,
};

export default pushNotifications;
